"use client";

import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { type SearchableFilterOption } from "./types";
import { filterSegmentTriggerClassName } from "./shared";
import { Icon } from "@/components/ui/icon";

/** Option avatar with a letter fallback that also covers image load errors. */
function OptionAvatar({
  src,
  label,
  size,
}: {
  src?: string | null;
  label: string;
  size: number;
}) {
  return (
    <Avatar
      className="shrink-0 rounded-full"
      style={{ width: size, height: size }}
    >
      {src && <AvatarImage src={src} alt="" className="object-cover" />}
      <AvatarFallback
        className="rounded-full bg-contrast/8 font-medium text-contrast/50"
        style={{ fontSize: size <= 14 ? 8 : 9 }}
      >
        {label.charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

export function SearchableSegment({
  value,
  onChange,
  options,
  placeholder = "Select…",
  presentation = "member",
}: {
  value: string;
  onChange: (v: string) => void;
  options: SearchableFilterOption[];
  placeholder?: string;
  presentation?: "member" | "label";
}) {
  const isLabelPresentation = presentation === "label";
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const selected = useMemo(
    () => options.find((o) => o.id === value),
    [options, value],
  );

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.sublabel?.toLowerCase().includes(q),
    );
  }, [search, options]);

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 280),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || dropdownRef.current?.contains(t))
        return;
      setOpen(false);
      setSearch("");
    };
    const handleScroll = () => updatePos();
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open, updatePos]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (!open) updatePos();
          setOpen((p) => !p);
        }}
        className={cn(
          filterSegmentTriggerClassName,
          "w-full max-w-[180px] justify-between text-contrast/75",
        )}
      >
        {selected ? (
          <span className="flex min-w-0 flex-1 items-center gap-1">
            <OptionAvatar
              src={selected.avatarUrl}
              label={selected.label}
              size={14}
            />
            <span className="min-w-0 truncate">{selected.label}</span>
          </span>
        ) : (
          <span className="min-w-0 flex-1 truncate text-contrast/40">
            {placeholder}
          </span>
        )}
        <Icon
          name="IconChevronGrabberVertical"
          size={10}
          className="shrink-0 text-heading opacity-20"
        />
      </button>

      {open &&
        pos &&
        createPortal(
          <motion.div
            ref={dropdownRef}
            className={cn(
              "fixed overflow-hidden rounded-lg border border-border bg-surface-elevated shadow-[var(--modal-shadow)]",
              isLabelPresentation && "w-max max-w-[calc(100vw-24px)]",
            )}
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              type: "spring",
              damping: 28,
              stiffness: 400,
              mass: 0.7,
            }}
            style={{
              top: pos.top,
              left: pos.left,
              width: isLabelPresentation ? undefined : pos.width,
              minWidth: isLabelPresentation
                ? Math.max(pos.width, 280)
                : undefined,
              zIndex: 9999,
            }}
          >
            <div className="flex items-center gap-1.5 px-2.5 py-2">
              <Icon
                name="IconMagnifyingGlass"
                size={14}
                className="text-heading opacity-50 shrink-0"
              />
              <Input
                ref={searchRef}
                type="text"
                variant="ghost"
                size="none"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="flex-1 text-sm"
              />
            </div>
            <div className="h-px bg-contrast/[0.12]" />
            <div
              className={cn(
                "max-h-[200px] overflow-y-auto py-1",
                isLabelPresentation &&
                  "flex w-max min-w-full flex-col overflow-x-hidden",
              )}
            >
              {filtered.length === 0 ? (
                <div className="flex items-center justify-center h-[60px] text-[13px] text-contrast/40">
                  No results
                </div>
              ) : (
                filtered.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onChange(o.id);
                      setOpen(false);
                      setSearch("");
                    }}
                    className={cn(
                      buttonVariants({ variant: "ghost" }),
                      "h-auto justify-start gap-2 rounded-none px-3 py-[5px] text-[13px] font-normal",
                      o.id === value && "bg-contrast/4",
                      isLabelPresentation
                        ? "flex w-max min-w-full items-center justify-between"
                        : "w-full",
                    )}
                  >
                    <OptionAvatar src={o.avatarUrl} label={o.label} size={18} />
                    <span className="text-heading truncate flex-1 text-left">
                      {o.label}
                    </span>
                    {o.sublabel && (
                      <span className="text-contrast/40 shrink-0 text-xs">
                        {o.sublabel}
                      </span>
                    )}
                    {o.id === value && (
                      <Icon
                        name="IconCheckmark1"
                        size={14}
                        className="text-heading shrink-0"
                      />
                    )}
                  </button>
                ))
              )}
            </div>
          </motion.div>,
          document.body,
        )}
    </>
  );
}
